import { memo, useCallback } from 'react';

import { Button, CopyText, Flex, Heading, Label, Text, TruncatedText } from '@/components/core';
import { HowToGuide } from '@/components/flow';
import { WarningDuotoneIcon } from '@/components/icons';
import { ZapierLogo } from '@/components/logos';
import { useGetAgentFromPath } from '@/hooks';
import { toAppClientId, useGetAppClient, useListAppClients } from '@/hooks/api';
import { useTriggerStore } from '@/store';

export type ExportZapierConfigurationProps = object;

const ZAPIER_BASE_URI = 'https://api.zapier.com/v1/embed/cradl-ai/create';

// eslint-disable-next-line no-empty-pattern
const IExportZapierConfiguration = ({}: ExportZapierConfigurationProps = {}) => {
  const action = useTriggerStore((state) => state.action);
  const update = useTriggerStore((state) => state.updateAction);
  const { data: agent } = useGetAgentFromPath();

  const { data: appClients } = useListAppClients();
  const appClientWithSecret = appClients?.data?.find((appClient) => appClient.hasSecret);

  const { data: appClient } = useGetAppClient(toAppClientId(appClientWithSecret?.appClientId), {
    refetchInterval: (query) => (!query.state.data?.clientSecret ? 2000 : undefined),
  });

  const isConfigured = !!action.config.url;

  const onDisconnect = useCallback(() => {
    update({ config: { ...action.config, url: null } });
  }, [action.config, update]);

  const newZapUrl = `${ZAPIER_BASE_URI}?steps[0][app]=CradlAiCLIAPI@latest&steps[0][action]=agentRunComplete&steps[0][params][agentId]=${agent?.agentId}&steps[0][params][actionId]=${action?.actionId}`;

  const onSetupClicked = useCallback(() => {
    window.open(newZapUrl, '_blank');
  }, [newZapUrl]);

  return (
    <Flex col className="gap-md p-md grow">
      <Flex col className="gap-sm">
        <div className="gap-sm flex items-center">
          <Text size="xs" className="font-semibold text-gray-600">
            Account
          </Text>

          <div className="grow" />

          {isConfigured ? (
            <>
              <div className="border-success-100 bg-success-500 size-3 rounded-full border-3" />
              <Text size="xs" className="text-success-700 font-semibold">
                Connected
              </Text>
            </>
          ) : (
            <>
              <div className="size-3 rounded-full border-3 border-gray-100 bg-gray-500" />
              <Text size="xs" className="font-semibold text-gray-500">
                Not connected
              </Text>
            </>
          )}
        </div>

        <Flex className="p-sm gap-sm bg-gray-25 items-center rounded-md border-1 border-gray-100">
          <ZapierLogo className="shrink-0" size="xl" />

          <TruncatedText className="grow-0">{isConfigured ? action.config?.url : 'Sign in to connect'}</TruncatedText>

          {isConfigured ? (
            <Button className="shrink-0" variant="secondary" onPress={onDisconnect}>
              <Text>Disconnect</Text>
            </Button>
          ) : (
            <Button className="shrink-0" variant="primary" onPress={onSetupClicked}>
              <Text>Sign in</Text>
            </Button>
          )}
        </Flex>
      </Flex>

      {!isConfigured ? (
        <>
          <Flex variant="plain" col className="p-md gap-4 rounded-lg border-1 border-gray-100 bg-white shadow-xs">
            <Flex col className="gap-xs">
              <Label>Client ID</Label>
              <CopyText value={appClient?.clientId} />
            </Flex>

            <Flex col className="gap-xs">
              <Label>Client Secret</Label>
              <CopyText isSensitive value={appClient?.clientSecret} />
            </Flex>
          </Flex>

          <Flex variant="warning" className="p-md gap-sm rounded-lg">
            <WarningDuotoneIcon />

            <Flex col className="border-none">
              <Heading size="sm">Setup required</Heading>
              <Text>
                Make sure your Zap is connected and <strong>published</strong>.
              </Text>
            </Flex>
          </Flex>
        </>
      ) : null}

      <div className="grow" />

      <HowToGuide actionId={action.actionId} />
    </Flex>
  );
};

export const ExportZapierConfiguration = memo(IExportZapierConfiguration) as typeof IExportZapierConfiguration;
